/**
 * 魔法カード有効性チェッカー
 *
 * AIが魔法カードを発動する前に、そのカードが実際に効果を発揮できるか
 * （条件を満たしているか）を事前チェックするシステム
 *
 * 問題背景:
 * - 「奈落の呼び声」は墓地にコスト4以下闇属性モンスターがないと効果なし
 * - 「ネクロフィアの儀式」は場にモンスター+デッキにネクロフィアがないと効果なし
 * - AIがコストだけ見て発動し、効果なしでSPを無駄にしていた
 *
 * 解決策:
 * - AIが魔法カードを選択する前に「効果が有効か」をチェック
 * - 有効でない魔法カードは使用可能リストから除外
 */

import type { CardWithId, MonsterInstance, CardAttribute } from '../../types/card';
import type { AIGameState } from './strategies/base';
import { hasHikou } from '../keywordAbilities';

/** 魔法カード有効性チェッカー関数型 */
type MagicChecker = (card: CardWithId, gameState: AIGameState) => boolean;

// ============================
// ヘルパー関数
// ============================

/**
 * 場に特定の名前を含むモンスターがいるかチェック
 */
const hasMonsterWithName = (field: (MonsterInstance | null)[], namePart: string): boolean => {
  return field.some(slot => slot !== null && slot.name?.includes(namePart));
};

/**
 * 場に特定のカテゴリを含むモンスターがいるかチェック
 */
const hasMonsterWithCategory = (field: (MonsterInstance | null)[], categoryPart: string): boolean => {
  return field.some(slot => slot !== null && slot.category?.includes(categoryPart));
};

/**
 * 場に特定の属性のモンスターがいるかチェック
 */
const hasMonsterWithAttribute = (field: (MonsterInstance | null)[], attribute: CardAttribute): boolean => {
  return field.some(slot => slot !== null && slot.attribute === attribute);
};

/**
 * 場にモンスターがいるかチェック
 */
const hasAnyMonster = (field: (MonsterInstance | null)[]): boolean => {
  return field.some(slot => slot !== null);
};

/**
 * 場のモンスター数を数える
 */
const countMonsters = (field: (MonsterInstance | null)[]): number => {
  return field.filter(slot => slot !== null).length;
};

/**
 * デッキに特定の名前のカードがあるかチェック
 */
const hasDeckCardWithName = (deck: CardWithId[], name: string): boolean => {
  return deck.some(c => c.name === name);
};

/**
 * デッキに特定の名前を含むカードがあるかチェック
 */
const hasDeckCardIncludingName = (deck: CardWithId[], namePart: string): boolean => {
  return deck.some(c => c.name?.includes(namePart));
};

/**
 * 場に飛行モンスターがいるかチェック
 * (キーワードまたはeffectに「飛行」を含むカード)
 */
const hasMonsterWithFlight = (field: (MonsterInstance | null)[]): boolean => {
  return field.some(slot => {
    if (!slot) return false;
    // キーワードに飛行があるか
    if (slot.keyword?.includes('飛行')) return true;
    // 効果テキストに【飛行】があるか（CSVからパースされた場合）
    if (slot.effect?.includes('【飛行】')) return true;
    return false;
  });
};

// ============================
// カードIDごとの有効性チェッカー
// チェッカーが未定義のカードはデフォルトで有効とみなす
// ============================
const magicEffectivityCheckers: Record<string, MagicChecker> = {
  // ============================
  // 粘液獣系
  // ============================

  /**
   * C0000012: 粘液の増殖
   * 場にいる《粘液獣》1体を分裂させる（攻撃力は半分になる）。
   * → 場に粘液獣が必要 + 空きスロットが必要
   */
  C0000012: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, '粘液獣')) {
      return false;
    }
    // 分裂するので空きスロットが必要
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000013: 粘液の罠【刹那詠唱】
   * 相手モンスター攻撃時、発動可能。そのモンスターの攻撃力を800ダウン、次のターンまで行動不能にする。
   * → 相手場にモンスターが必要
   */
  C0000013: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000014: 粘液再生
   * 墓地の《粘液獣》2体を場に戻す（攻撃力半分・HP半分・効果無効になる）。
   * → 墓地に粘液獣が2体必要 + 場に空きが2つ必要
   */
  C0000014: (card, gameState) => {
    const slimeMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.name?.includes('粘液獣')
    );
    if (slimeMonsters.length < 2) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length >= 2;
  },

  /**
   * C0000015: 粘液の浸食【刹那詠唱】
   * 相手の場にいるモンスター1体の攻撃力を毎ターン500ダウン、0になったら破壊する。
   * → 相手場にモンスターが必要
   */
  C0000015: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000016: 粘液爆発
   * 場にいる《粘液獣》1体を破壊し、相手モンスター全体にその攻撃力分のダメージを与える。
   * → 場に粘液獣が必要 + 相手場にモンスターが必要
   */
  C0000016: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, '粘液獣')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000017: 未来の粘液回路
   * デッキから《粘液獣・キング》または《新・超覚醒粘液獣－ハイパー－》を場に直接召喚。
   * → デッキに対象カードが必要 + 場に空きが必要
   */
  C0000017: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.name === '粘液獣・キング' || c.name === '新・超覚醒粘液獣－ハイパー－'
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  // ============================
  // 炎・ドラゴン系
  // ============================

  /**
   * C0000022: 火竜の吐息【刹那詠唱】
   * 相手のモンスター1体に1000ダメージを与える。相手プレイヤーに500ダメージを与える。
   * → 相手場にモンスターが必要（モンスター対象効果）
   */
  C0000022: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000024: 紅蓮爆発
   * 手札を全て捨てる。自分の場にいるモンスターの攻撃力を倍にする。
   * → 自場にモンスターが必要
   */
  C0000024: (card, gameState) => {
    return hasAnyMonster(gameState.myField);
  },

  /**
   * C0000031: 炎の咆哮
   * 場にいる［ドラゴン］と名の付いたモンスター1体の攻撃力をターン終了時まで500アップし、相手プレイヤーに300ダメージを与える。
   * → 場にドラゴンモンスターが必要
   */
  C0000031: (card, gameState) => {
    return hasMonsterWithCategory(gameState.myField, 'ドラゴン');
  },

  /**
   * C0000032: ドラゴンの炎息【刹那詠唱】
   * 相手モンスター1体に800ダメージを与えた後、相手モンスター全体に300ダメージを与える。
   * → 相手場にモンスターが必要
   */
  C0000032: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000033: 紅蓮の覚醒
   * 場にいる［ドラゴン］モンスター1体を、このターン中「覚醒」状態にし、攻撃力を700アップ。
   * → 場にドラゴンモンスターが必要
   */
  C0000033: (card, gameState) => {
    return hasMonsterWithCategory(gameState.myField, 'ドラゴン');
  },

  /**
   * C0000034: 炎の嵐
   * 場にいる全ての［ドラゴン］モンスターの数×200ダメージを相手プレイヤーに与える。
   * → 場にドラゴンモンスターがいないと0ダメージで無意味
   */
  C0000034: (card, gameState) => {
    return hasMonsterWithCategory(gameState.myField, 'ドラゴン');
  },

  /**
   * C0000035: ドラゴンの血脈
   * デッキから《レッドバーストドラゴン》または《炎竜母フレイマ》を場に直接召喚。
   * → デッキに対象カードが必要 + 場に空きが必要
   */
  C0000035: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.name === 'レッドバーストドラゴン' || c.name === '炎竜母フレイマ'
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000036: 燃え尽きる業火
   * 自分の場にいる《ドラゴン》と名のついたモンスター1体を破壊し、その攻撃力分のダメージを相手プレイヤーに与える。
   * → 場にドラゴンモンスターが必要
   */
  C0000036: (card, gameState) => {
    return hasMonsterWithCategory(gameState.myField, 'ドラゴン');
  },

  // ============================
  // 水属性系
  // ============================

  /**
   * C0000048: 人魚の波誘い【刹那詠唱】
   * 相手モンスター1体の攻撃先を別の相手モンスターに変更する。
   * → 相手場にモンスターが2体以上必要（攻撃先を変更するため）
   */
  C0000048: (card, gameState) => {
    return countMonsters(gameState.opponentField) >= 2;
  },

  /**
   * C0000049: タイダルシフト
   * 場にいる水属性モンスター1体をデッキに戻し、デッキから同じコスト以下の別の水属性モンスターを場に出す。
   * → 場に水属性モンスターが必要
   */
  C0000049: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '水');
  },

  /**
   * C0000050: クラーケンの呼び声【刹那詠唱】
   * 墓地の水属性モンスター1体を手札に戻し、そのカードの召喚コストを1軽減。
   * → 墓地に水属性モンスターが必要
   */
  C0000050: (card, gameState) => {
    const validMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.attribute === '水'
    );
    return validMonsters.length > 0;
  },

  /**
   * C0000051: リヴァイアサンの奔流
   * 相手モンスター全体に、場にいる水属性モンスターの数×400ダメージを与える。
   * → 場に水属性モンスターが必要 + 相手場にモンスターが必要
   */
  C0000051: (card, gameState) => {
    if (!hasMonsterWithAttribute(gameState.myField, '水')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000052: ネプチューンの加護
   * このターン、自分の水属性モンスター全ての攻撃力を600アップ。次のターン終了時まで、自分の水属性モンスター全てへの技のダメージが0になる。
   * → 場に水属性モンスターが必要
   */
  C0000052: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '水');
  },

  // ============================
  // 光属性系
  // ============================

  /**
   * C0000064: ルミナスの裁き【刹那詠唱】
   * 光以外の相手モンスター1体を破壊する。自分の場に光属性モンスターが2体以上いる場合自分はその攻撃力の半分回復する。
   * → 相手場に光以外のモンスターが必要
   */
  C0000064: (card, gameState) => {
    return gameState.opponentField.some(
      slot => slot !== null && slot.attribute !== '光'
    );
  },

  /**
   * C0000065: 天使の波動【刹那詠唱】
   * 相手モンスター全体に、場にいる光属性モンスターの数×300ダメージを与える。
   * → 自場に光属性モンスターが必要 + 相手場にモンスターが必要
   */
  C0000065: (card, gameState) => {
    if (!hasMonsterWithAttribute(gameState.myField, '光')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000066: ホーリーライトサモン
   * デッキからランダムなコスト3以下の光属性モンスター1体を場に出す（コスト不要）。
   * → デッキにコスト3以下の光属性モンスターが必要 + 場に空きが必要
   */
  C0000066: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.type === 'monster' && c.attribute === '光' && (c.cost || 0) <= 3
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000067: ルミナス・ドミネーション
   * 相手の場にいるモンスター1体のコントロールをターン終了時まで奪う。そのモンスターの攻撃力は半分になる。
   * → 相手場にモンスターが必要 + 自場に空きが必要
   */
  C0000067: (card, gameState) => {
    if (!hasAnyMonster(gameState.opponentField)) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000069: 聖なる浄化
   * 場にいる全てのモンスターの効果をターン終了時まで無効化し、場のモンスター全体に2000ダメージを与える。
   * → 場にモンスターがいないと無意味（自分か相手どちらか）
   */
  C0000069: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000070: 支配光の連鎖
   * 自分の場に光属性モンスターがいる場合、相手の手札を1枚ランダムに捨てる。
   * → 自場に光属性モンスターが必要
   */
  C0000070: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '光');
  },

  // ============================
  // 闇属性系
  // ============================

  /**
   * C0000075: シャドウ・バインド【刹那詠唱】
   * 相手のモンスター1体を動けなくする（1ターン行動不能）。
   * → 相手場にモンスターが必要
   */
  C0000075: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000083: 闇の契約
   * 自分のライフを500減らす。デッキからコスト4以下闇属性モンスター1体を手札に加える。
   * → デッキにコスト4以下闇属性モンスターが必要
   */
  C0000083: (card, gameState) => {
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.attribute === '闇' && (c.cost || 0) <= 4
    );
  },

  /**
   * C0000084: 腐食のコロード
   * 場にいるモンスター1体の攻撃力を800ダウン、0になると破壊。このターン、自分の闇属性モンスターの攻撃力を300アップ。
   * → 場にモンスターが必要（自分か相手どちらか）
   */
  C0000084: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000086: 闇の波動
   * 相手プレイヤーに場にいる闇属性モンスターの数×400ダメージを与える。自分のライフを300減らす。
   * → 自場に闇属性モンスターが必要（0体だと0ダメージで無意味）
   */
  C0000086: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '闇');
  },

  /**
   * C0000087: シャドウ・チェインズ【刹那詠唱】
   * 相手モンスター1体の攻撃力をターン終了時まで0にし、相手モンスター1体に400ダメージを与える。
   * → 相手場にモンスターが必要
   */
  C0000087: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000093: ダーク・ヴォイド
   * 場にいるモンスター1体に1600ダメージを与える。
   * → 場にモンスターが必要（自分か相手どちらか）
   */
  C0000093: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000100: 闇の収集
   * 相手の墓地のカードを2枚除外し、デッキから闇属性モンスター1枚を手札に加える。
   * → 相手墓地に2枚以上 + デッキに闇属性モンスター
   */
  C0000100: (card, gameState) => {
    if (gameState.opponentGraveyard.length < 2) {
      return false;
    }
    return gameState.myDeck.some(c => c.type === 'monster' && c.attribute === '闇');
  },

  /**
   * C0000101: シャドウ・カース
   * 相手モンスター1体の攻撃力をターン終了時まで0にし、そのモンスターの効果を次のターン終了時まで無効化。
   * → 相手場にモンスターが必要
   */
  C0000101: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000102: 暗黒の略奪
   * 手札を1枚捨てる。そのコスト以下の相手の場にいる禁忌カード以外のモンスター1体のコントロールを奪う。
   * → 手札2枚以上 + 相手場に禁忌以外のモンスター + 自場に空き
   */
  C0000102: (card, gameState) => {
    // 手札2枚以上必要（1枚捨てる + 魔法カード自身）
    if (gameState.myHand.length < 2) {
      return false;
    }
    // 相手場に禁忌以外のモンスターが必要
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && !slot.forbidden
    );
    if (!hasValidTarget) {
      return false;
    }
    // 自場に空きが必要
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000104: 闇の崩壊
   * 場にいる全てのモンスターの攻撃力を半分にし、相手モンスター全体にその差分の合計ダメージを与える。
   * → 相手場にモンスターが必要
   */
  C0000104: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000117: 禁忌の波動
   * 場にいる全てのモンスターの効果をターン終了時まで無効化し、相手モンスター全体に場の闇属性モンスター1体につき400ダメージを与える。
   * → 自場に闇属性モンスター + 相手場にモンスターが必要
   */
  C0000117: (card, gameState) => {
    if (!hasMonsterWithAttribute(gameState.myField, '闇')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000118: シャドウ・ドミニオン
   * 手札からカード1枚と追加で闇属性カードを任意の枚数捨てる。追加で捨てたカード1枚につき1体、禁忌カード以外の相手の場にいるモンスターのコントロールをターン終了時まで奪う。
   * → 手札2枚以上 + 相手場に禁忌以外のモンスター + 自場に空き
   */
  C0000118: (card, gameState) => {
    // 手札2枚以上必要（1枚必須 + 追加の闇属性）
    if (gameState.myHand.length < 2) {
      return false;
    }
    // 相手場に禁忌以外のモンスターが必要
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && !slot.forbidden
    );
    if (!hasValidTarget) {
      return false;
    }
    // 自場に空きが必要
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000119: 闇の宣告
   * 相手の手札を全て公開し、その中から1枚を選んで墓地に送る。このターン、自分の闇属性モンスターの攻撃力を400アップ。
   * → 相手手札が1枚以上必要
   */
  C0000119: (card, gameState) => {
    return gameState.opponentHandCount > 0;
  },

  /**
   * C0000120: 禁忌の儀式
   * 自分の場にいるモンスター2体をリリースし、デッキから《ブラック・オラクル》を場に直接召喚（コスト不要）。
   * → 自場にモンスター2体以上 + デッキにブラック・オラクル
   */
  C0000120: (card, gameState) => {
    if (countMonsters(gameState.myField) < 2) {
      return false;
    }
    return hasDeckCardWithName(gameState.myDeck, 'ブラック・オラクル');
  },

  // ============================
  // 闇属性・蘇生系
  // ============================

  /**
   * C0000088: ネクロフィアの儀式
   * 自分の場にいるモンスター1体をリリースし、デッキから《ダーク・ネクロフィア》を場に直接召喚。
   * → 場にモンスターが必要 + デッキにダーク・ネクロフィアが必要
   */
  C0000088: (card, gameState) => {
    if (!hasAnyMonster(gameState.myField)) {
      return false;
    }
    return hasDeckCardWithName(gameState.myDeck, 'ダーク・ネクロフィア');
  },

  /**
   * C0000103: 奈落の呼び声
   * 自分の墓地のコスト4以下闇属性モンスター1体を場に戻し、攻撃力を600アップ。
   * → 墓地にコスト4以下闇属性モンスターが必要 + 場に空きが必要
   */
  C0000103: (card, gameState) => {
    const validMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' &&
           c.attribute === '闇' &&
           (c.cost || 0) <= 4
    );
    if (validMonsters.length === 0) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  // ============================
  // 全体ダメージ系（相手場にモンスター必要）
  // ============================

  /**
   * C0000388: 黒呪・ファルザノンの影爪【刹那詠唱】
   * 自分のデッキの上から1枚を墓地に送り相手モンスター1体に1000ダメージを与える。
   * → 相手場にモンスター
   */
  C0000388: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000389: 黒呪・ザルガノートの呪鎖【刹那詠唱】
   * 相手モンスター1体を次のターン終了時まで行動不能にし、毎ターン終了時400ダメージ。
   * → 相手場にモンスター
   */
  C0000389: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000390: 黒呪・ヴェルザクトの黒刃【刹那詠唱】
   * 相手モンスター1体に1200ダメージを与え、攻撃力を500下げる。
   * → 相手場にモンスター
   */
  C0000390: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000391: 黒呪・トゥルヴァントの障壁【刹那詠唱】
   * 自分のモンスター1体に「守護」を付与する（1度だけ受けるダメージを半減）。
   * → 自場にモンスター
   */
  C0000391: (card, gameState) => {
    return hasAnyMonster(gameState.myField);
  },

  /**
   * C0000392: 黒呪・ドルヴァネクの傀儡糸
   * 相手のコスト4以下のモンスター1体のコントロールをターン終了時まで奪う。
   * → 相手場にコスト4以下のモンスター + 自場に空き
   */
  C0000392: (card, gameState) => {
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && (slot.cost || 0) <= 4
    );
    if (!hasValidTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000393: 黒呪・カルヴェリオンの灰嵐
   * 相手フィールド全体に1500ダメージを与え、次のターン終了時まで相手モンスターの攻撃力を500下げる。
   * → 相手の場にモンスターが必要
   */
  C0000393: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000394: 黒呪・エルザフィールの契約
   * 相手の場全体に2500ダメージを与える。
   * → 相手場にモンスター
   */
  C0000394: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000402: 呪術狩りの封印鎖【刹那詠唱】
   * 相手のモンスター1体をターン終了時まで行動不能にする。
   * → 相手場にモンスター
   */
  C0000402: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000428: 血焔の嵐
   * 相手フィールド全体に800ダメージを与える。自分のライフが3000以下の場合、1200に変更。
   * → 相手の場にモンスターが必要
   */
  C0000428: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 単体対象系（相手場にモンスター必要）
  // ============================

  /**
   * C0000429: 黒涙のうねり【刹那詠唱】
   * 相手モンスター1体のHPを1000減らし、次のターン終了時までその攻撃力を300ダウン。
   * → 相手の場にモンスターが必要
   */
  C0000429: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000431: 灰塵の刻印【刹那詠唱】
   * 相手モンスター1体を攻撃不能にする。このターン中、そのモンスターが受けるダメージを300アップ。
   * → 相手の場にモンスターが必要
   */
  C0000431: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000433: 灰光の裁き
   * 相手モンスター1体に800ダメージを与える。そのモンスターが闇属性の場合、1600に変更。
   * → 相手の場にモンスターが必要
   */
  C0000433: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000430: 狼王の咆哮
   * 自分の墓地の狼カード1枚を手札に戻す。そのカードのコストを1下げる。
   * → 墓地に狼カード
   */
  C0000430: (card, gameState) => {
    return gameState.myGraveyard.some(c => c.name?.includes('狼'));
  },

  /**
   * C0000432: 棘根の罠
   * 相手モンスター1体の攻撃力を次のターン終了時まで600ダウン。
   * → 相手場にモンスター
   */
  C0000432: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 原始属性系
  // ============================

  /**
   * C0000133: 鎖の舞踏【刹那詠唱】
   * 場にいるモンスター1体の攻撃力をターン終了時まで0にし、そのモンスターが破壊されるまで、毎ターン相手プレイヤーに300ダメージを与える。
   * → 場にモンスターが必要（自分か相手どちらか）
   */
  C0000133: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000134: ゴシックの誘惑
   * 相手モンスター1体を手札に戻す。
   * → 相手場にモンスターが必要
   */
  C0000134: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000135: 檻の牢獄
   * 相手の場にいるモンスター1体を2ターン行動不能にし、そのモンスターの攻撃力の半分を相手プレイヤーにダメージとして与える。
   * → 相手場にモンスターが必要
   */
  C0000135: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000136: 鎖の呪縛【刹那詠唱】
   * 相手モンスター1体に1500ダメージを与える。
   * → 相手場にモンスターが必要
   */
  C0000136: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000137: ゴシック・レクイエム
   * 場にいる《ゴシック》モンスター1体を破壊し、その攻撃力の2倍のダメージを相手モンスター全体に与える。
   * → 場にゴシックモンスター + 相手場にモンスターが必要
   */
  C0000137: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'ゴシック')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 水属性系（追加）
  // ============================

  /**
   * C0000150: 氷の吐息【刹那詠唱】
   * 相手モンスター1体の攻撃力を800下げ、ターン終了時まで「凍結」にする。
   * → 相手場にモンスターが必要
   */
  C0000150: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000151: ブリザードの呼び声
   * デッキからコスト3以下の《ブリザードキャット》モンスター1体を場に召喚（コスト不要）。
   * → デッキにコスト3以下のブリザードキャット + 場に空き
   */
  C0000151: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('ブリザードキャット') && (c.cost || 0) <= 3
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000152: 凍てつく爪痕
   * 場にいる【ビースト・猫】1体の攻撃力分のダメージを相手モンスターに与える。
   * → 自場にビースト・猫 + 相手場にモンスター
   */
  C0000152: (card, gameState) => {
    if (!hasMonsterWithCategory(gameState.myField, 'ビースト・猫')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000153: 氷猫の遊戯【刹那詠唱】
   * 場に《ブリザードキャット》モンスターがいるとき、コスト3以下の相手モンスター1体のコントロールを奪う。
   * → 自場にブリザードキャット + 相手場にコスト3以下のモンスター + 自場に空き
   */
  C0000153: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'ブリザードキャット')) {
      return false;
    }
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && (slot.cost || 0) <= 3
    );
    if (!hasValidTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000154: ブリザードの覚醒
   * 場にいる《ブリザードキャット》モンスター1体の攻撃力をターン終了時まで倍にする。
   * → 自場にブリザードキャットが必要
   */
  C0000154: (card, gameState) => {
    return hasMonsterWithName(gameState.myField, 'ブリザードキャット');
  },

  /**
   * C0000158: 聖光の刃
   * 相手のフィールドカード1枚を破壊する。場に光属性モンスターが1体以上いる場合、SP回復。
   * → 相手にフィールドカードが必要
   */
  C0000158: (card, gameState) => {
    return gameState.opponentFieldCard !== null;
  },

  /**
   * C0000159: 潮の還流
   * 自分の墓地の水属性モンスター2体をデッキに戻す。その後1枚ドロー。
   * → 墓地に水属性モンスター2体以上
   */
  C0000159: (card, gameState) => {
    const waterMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.attribute === '水'
    );
    return waterMonsters.length >= 2;
  },

  /**
   * C0000160: 業火の爆炎
   * 自分の場にいる炎属性モンスター1体を破壊し、その攻撃力の半分を相手プレイヤーに与える。
   * → 自場に炎属性モンスターが必要
   */
  C0000160: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '炎');
  },

  // ============================
  // 炎属性・岩狸系
  // ============================

  /**
   * C0000173: 熔岩の呼び声
   * デッキからコスト3以下の【マグマフォージ】モンスター1体を場に召喚（コスト不要）。
   * → デッキにコスト3以下のマグマフォージ + 場に空き
   */
  C0000173: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.type === 'monster' && c.category?.includes('マグマフォージ') && (c.cost || 0) <= 3
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000174: 岩狸の咆哮
   * 場にいる【ビースト・狸】モンスター1体の攻撃力をアップし、相手モンスター1体の攻撃力をダウン。
   * → 自場にビースト・狸 + 相手場にモンスター
   */
  C0000174: (card, gameState) => {
    if (!hasMonsterWithCategory(gameState.myField, 'ビースト・狸')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000175: 地層の炎流
   * 場にいる炎属性モンスターの数×300ダメージを相手プレイヤーに与える。
   * → 自場に炎属性モンスターが必要（0体だと0ダメージで無意味）
   */
  C0000175: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '炎');
  },

  /**
   * C0000176: 熔岩の罠【刹那詠唱】
   * このターンに召喚されたモンスターの攻撃力を半分にし、次のターンまで行動不能にする。
   * → 場にモンスターが必要（対象がいなければ無意味）
   */
  C0000176: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000177: 岩石の守護
   * このターン、自分の炎属性モンスター1体はダメージを受けない。
   * → 自場に炎属性モンスターが必要
   */
  C0000177: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '炎');
  },

  /**
   * C0000178: 狸火の誘い【刹那詠唱】
   * 相手モンスター1体の攻撃先を自分の別のモンスターに変更し、その相手モンスターの攻撃力を500ダウン。
   * → 自場にモンスター2体以上 + 相手場にモンスター
   */
  C0000178: (card, gameState) => {
    if (countMonsters(gameState.myField) < 2) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000179: 熔岩再生
   * 墓地のコスト4以下の《岩狸》モンスター2体を場に戻す（攻撃力は半分、HPは半分）。
   * → 墓地にコスト4以下の岩狸モンスター2体以上 + 場に空き2つ
   */
  C0000179: (card, gameState) => {
    const validMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.name?.includes('岩狸') && (c.cost || 0) <= 4
    );
    if (validMonsters.length < 2) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length >= 2;
  },

  /**
   * C0000180: 地割れの怒り
   * 場にいる【マグマフォージ】モンスター1体を破壊し、その攻撃力の半分＋1000ダメージを相手に与える。
   * → 自場にマグマフォージが必要
   */
  C0000180: (card, gameState) => {
    return hasMonsterWithCategory(gameState.myField, 'マグマフォージ');
  },

  /**
   * C0000181: 岩狸の覚醒
   * 場にいる炎属性モンスター1体の攻撃力をターン終了時まで倍にする。
   * → 自場に炎属性モンスターが必要
   */
  C0000181: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '炎');
  },

  /**
   * C0000182: 熔岩の奔流
   * 場にいる炎属性モンスターの数×300ダメージを自分と相手に与える。
   * → 自場に炎属性モンスターが必要（0体だと0ダメージで無意味）
   */
  C0000182: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '炎');
  },

  /**
   * C0000184: 狸の業火
   * 自分の場にいる《岩狸》モンスター1体を破壊し、その攻撃力分のダメージを相手に与える。
   * → 自場に岩狸モンスターが必要
   */
  C0000184: (card, gameState) => {
    return hasMonsterWithName(gameState.myField, '岩狸');
  },

  // ============================
  // なし属性系
  // ============================

  /**
   * C0000194: 急ごしらえの罠【刹那詠唱】
   * 相手モンスターの攻撃力をターン終了時まで500下げ、そのモンスターに500ダメージを与える。
   * → 相手場にモンスターが必要
   */
  C0000194: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000195: 交換の手札
   * 自分の手札を1枚デッキに戻し、デッキから2枚ドローする。
   * → 手札2枚以上必要（1枚戻す + 魔法カード自身）
   */
  C0000195: (card, gameState) => {
    return gameState.myHand.length >= 2;
  },

  /**
   * C0000196: 援軍の呼び声
   * デッキからコスト3以下のモンスター1体を場に召喚（コスト不要）。
   * → デッキにコスト3以下モンスター + 場に空き
   */
  C0000196: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.type === 'monster' && (c.cost || 0) <= 3
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000197: 力の均衡
   * 場にいる攻撃力2000以上のモンスター1体の攻撃力をターン終了時まで半分にする。
   * → 場に攻撃力2000以上のモンスターが必要（自分か相手どちらか）
   */
  C0000197: (card, gameState) => {
    const hasHighAtkMonster = (field: (MonsterInstance | null)[]) => {
      return field.some(slot => {
        if (slot === null) return false;
        const atk = slot.currentAttack || slot.attack || 0;
        return atk >= 2000;
      });
    };
    return hasHighAtkMonster(gameState.myField) || hasHighAtkMonster(gameState.opponentField);
  },

  /**
   * C0000198: 逆転の足音
   * 墓地のモンスター1体を自分の場に戻す（攻撃力は半分）。
   * → 墓地にモンスター + 場に空き
   */
  C0000198: (card, gameState) => {
    const hasMonsterInGraveyard = gameState.myGraveyard.some(c => c.type === 'monster');
    if (!hasMonsterInGraveyard) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000201: 地獄開城の契約【禁忌カード】
   * 自分のデッキからモンスターを3体まで場に召喚（コスト不要）。
   * → デッキにモンスター + 場に空き
   */
  C0000201: (card, gameState) => {
    const hasMonsterInDeck = gameState.myDeck.some(c => c.type === 'monster');
    if (!hasMonsterInDeck) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000202: 嵐の突風
   * 場に存在するフィールドカード1枚を破壊する。
   * → 相手のフィールドカードが存在（AIは自分のカード破壊を避ける）
   */
  C0000202: (card, gameState) => {
    return gameState.opponentFieldCard !== null;
  },

  /**
   * C0000203: 地盤崩し
   * 場に存在するフィールドカード1枚を破壊し、そのカードのプレイヤーに300ダメージを与える。
   * → 相手のフィールドカードが存在（AIは自分のカード破壊を避ける）
   */
  C0000203: (card, gameState) => {
    return gameState.opponentFieldCard !== null;
  },

  /**
   * C0000204: 崩れゆく礎
   * 自分の手札を1枚墓地に捨て、フィールドカードを破壊。
   * → 手札2枚以上 + 相手のフィールドカードが存在（AIは自分のカード破壊を避ける）
   */
  C0000204: (card, gameState) => {
    if (gameState.myHand.length < 2) {
      return false;
    }
    return gameState.opponentFieldCard !== null;
  },

  /**
   * C0000208: 逆転の賭け
   * 自分のライフが相手より少ない場合発動できる。
   * → 自分のライフが相手より少ない
   */
  C0000208: (card, gameState) => {
    return gameState.myLife < gameState.opponentLife;
  },

  /**
   * C0000209: 即興の連携【刹那詠唱】
   * 場にいる自分のモンスター1体の攻撃力をターン終了時まで800アップ。
   * → 自場にモンスターが必要
   */
  C0000209: (card, gameState) => {
    return hasAnyMonster(gameState.myField);
  },

  /**
   * C0000210: 静寂の帳【刹那詠唱】
   * このターン、場にいる全てのモンスターの効果を無効化する。
   * → 場にモンスターが必要（自分か相手どちらか）
   */
  C0000210: (card, gameState) => {
    return hasAnyMonster(gameState.myField) || hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 光属性・フルーツ/リリカ系
  // ============================

  /**
   * C0000216: フルーツの糸引き
   * 場に《フルーツ・マリオネット》モンスターがいるとき、禁忌カード以外の相手モンスター1体のコントロールを奪う。
   * → 自場にフルーツ・マリオネット + 相手場に禁忌以外のモンスター + 自場に空き
   */
  C0000216: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'フルーツ・マリオネット')) {
      return false;
    }
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && !slot.forbidden
    );
    if (!hasValidTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000217: 光果の舞踏【刹那詠唱】
   * 自分の場に光属性【プラント】モンスターがいるとき、自分のモンスターを破壊して手札から光属性プラントを召喚。
   * → 自場に光属性プラント + 手札に光属性プラントモンスター
   */
  C0000217: (card, gameState) => {
    const hasFieldPlant = gameState.myField.some(
      slot => slot !== null && slot.attribute === '光' && slot.category?.includes('プラント')
    );
    if (!hasFieldPlant) {
      return false;
    }
    return gameState.myHand.some(
      c => c.type === 'monster' && c.attribute === '光' && c.category?.includes('プラント')
    );
  },

  /**
   * C0000236: プリティ☆プリンセス
   * 場にいる《リリカ》モンスター1体の攻撃力をアップし、相手モンスターに300ダメージ。
   * → 自場にリリカ + 相手場にモンスター
   */
  C0000236: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'リリカ')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000237: 幼魔王女の誘惑【刹那詠唱】
   * 場に《リリカ》と名のつくモンスターがいるとき、禁忌カード以外の相手モンスター1体のコントロールを奪う。
   * → 自場にリリカ + 相手場に禁忌以外のモンスター + 自場に空き
   */
  C0000237: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'リリカ')) {
      return false;
    }
    const hasValidTarget = gameState.opponentField.some(
      slot => slot !== null && !slot.forbidden
    );
    if (!hasValidTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  // ============================
  // なし属性・蛮族/プニリーヌ系
  // ============================

  /**
   * C0000225: 鉄槍の奉仕
   * 《プニリーヌ》モンスターが場にいる場合、蛮族モンスター1体の攻撃力2倍。
   * → 自場にプニリーヌ + 場に蛮族モンスター
   */
  C0000225: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'プニリーヌ')) {
      return false;
    }
    return hasMonsterWithCategory(gameState.myField, '蛮族');
  },

  /**
   * C0000226: 順番待ちの苛立ち【刹那詠唱】
   * 相手モンスター1体の攻撃力を2000ダウン。
   * → 相手場にモンスター
   */
  C0000226: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000227: 鉄槍の昼餐
   * 墓地の蛮族モンスター2体を手札に加える。
   * → 墓地に蛮族モンスター2体以上
   */
  C0000227: (card, gameState) => {
    const barbarianMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.category?.includes('蛮族')
    );
    return barbarianMonsters.length >= 2;
  },

  /**
   * C0000228: 鉄槍の睡眠罠
   * 次ターン終了時まで相手モンスター全体の攻撃力を1000ダウンし、「眠り」状態にする。自分のプニリーヌはこのターン攻撃不可。
   * → 相手場にモンスター
   */
  C0000228: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000239: 散歩の思い出
   * 自分のモンスター1体のHPを500回復する。
   * → 自場にモンスター
   */
  C0000239: (card, gameState) => {
    return hasAnyMonster(gameState.myField);
  },

  /**
   * C0000243: ご主人様のおしおき【刹那詠唱】
   * 相手モンスター1体の攻撃力を0にし、行動不能状態にする。
   * → 相手場にモンスター
   */
  C0000243: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 原始属性・リアノン系
  // ============================

  /**
   * C0000238: おしおきの鎖【刹那詠唱】
   * 相手モンスター1体の攻撃力を800下げ、ターン終了時まで行動不能。
   * → 相手場にモンスター
   */
  C0000238: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000247: 鎖の共鳴
   * 場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいる場合発動可能。
   * → 自場にリアノン + 相手場にモンスター
   */
  C0000247: (card, gameState) => {
    const hasRianon = gameState.myField.some(
      slot => slot !== null && (
        slot.name === '鎖縛の幻姫リアノン' ||
        slot.name === '鎖縛の禁忌姫リアノン・エターナル'
      )
    );
    if (!hasRianon) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 未来属性系
  // ============================

  /**
   * C0000253: 未来の紫翼
   * デッキからコスト3以下の未来属性モンスター1体を場に召喚。
   * → デッキにコスト3以下未来属性モンスター + 場に空き
   */
  C0000253: (card, gameState) => {
    const hasTarget = gameState.myDeck.some(
      c => c.type === 'monster' && c.attribute === '未来' && (c.cost || 0) <= 3
    );
    if (!hasTarget) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000254: 時の歪曲【刹那詠唱】
   * 相手モンスター1体の攻撃力をターン終了時まで0にする。
   * → 相手場にモンスター
   */
  C0000254: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000259: 素晴らしい未来への誘導
   * 自分の未来モンスターの攻撃力を500アップ。
   * → 自場に未来属性モンスター（攻撃力アップの意味がある）
   */
  C0000259: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '未来');
  },

  /**
   * C0000261: アクセラレート
   * 自分の未来モンスターの基本技を2回使用可能。
   * → 自場に未来属性モンスター
   */
  C0000261: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '未来');
  },

  /**
   * C0000262: 時の遅延
   * 相手モンスターの基本技と上級技を封じる。
   * → 相手場にモンスター
   */
  C0000262: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000264: 時の逆流
   * 墓地に『未来』属性カード3枚あるとき発動可能。
   * 墓地の『未来』属性カード3枚をデッキに戻し、2枚ドローする。
   * 自分のライフを1500回復。自分のレスト状態のSPトークンを全てアクティブにする。
   * → 墓地に未来属性カード3枚以上のみが条件（相手モンスター不要）
   */
  C0000264: (card, gameState) => {
    const futureCards = gameState.myGraveyard.filter(c => c.attribute === '未来');
    return futureCards.length >= 3;
  },

  /**
   * C0000269: 永遠の灯の残響
   * 自分の墓地のコスト4以下「未来属性」モンスター1体を場に召喚。そのモンスターはターン終了時まで破壊されない。
   * → 墓地にコスト4以下未来属性モンスター + 場に空き
   */
  C0000269: (card, gameState) => {
    const validMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.attribute === '未来' && (c.cost || 0) <= 4
    );
    if (validMonsters.length === 0) {
      return false;
    }
    const emptySlots = gameState.myField.filter(slot => slot === null);
    return emptySlots.length > 0;
  },

  /**
   * C0000273: エクラリアの残影
   * 自分のデッキから《エクラリア》と名のついたモンスター1体を手札に加える。
   * → デッキにエクラリアと名のついたモンスター
   */
  C0000273: (card, gameState) => {
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('エクラリア')
    );
  },

  /**
   * C0000276: 大崩壊の刻
   * 自分のデッキ上3枚を墓地に送り、その中の「未来属性」カード1枚につき自分のレスト状態のSPトークンをアクティブにする。
   * → デッキ3枚以上（効果を発揮するため）
   */
  C0000276: (card, gameState) => {
    return gameState.myDeck.length >= 3;
  },

  /**
   * C0000277: 星屑の時導術【刹那詠唱】
   * 自分のデッキ上3枚を見て、1枚を手札に加え、残りを好きな順番で戻す。
   * → デッキ1枚以上（基本的に常に有効）
   */
  C0000277: (card, gameState) => {
    return gameState.myDeck.length >= 1;
  },

  // ============================
  // なし属性・毒系
  // ============================

  /**
   * C0000286: 毒草の調合
   * 自分のライフを600減らし、デッキから【ポイズンウェイル】モンスター1体を手札に加える。
   * → デッキにポイズンウェイルモンスター + ライフ600より多い（自傷死防止）
   */
  C0000286: (card, gameState) => {
    if (gameState.myLife <= 600) {
      return false;
    }
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.category?.includes('ポイズンウェイル')
    );
  },

  // C0000287 癒しの猛毒 - ライフ1000回復＋相手500ダメージ、条件なしで常に有効

  /**
   * C0000288: 酸の霧【刹那詠唱】
   * 相手モンスター1体の攻撃力をターン終了時まで0にする。
   * → 相手場にモンスター
   */
  C0000288: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 光属性・雷系
  // ============================

  /**
   * C0000323: 雷鳴の裁き【刹那詠唱】
   * 場にいるモンスター1体の攻撃力を1200下げ、次のターン終了まで攻撃不能にする。場に光属性モンスターがいる場合、相手モンスター全体に300ダメージを与える。
   * → 相手場にモンスター（AI的には相手を狙いたい）
   */
  C0000323: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000324: 嵐の咆哮
   * 場にいる光属性モンスターの数×400ダメージを相手モンスター全体に与える。このターン、自分の光属性モンスターの攻撃力が300アップ。
   * → 自場に光属性モンスター（0体だと0ダメージで無意味） + 相手場にモンスター
   */
  C0000324: (card, gameState) => {
    if (!hasMonsterWithAttribute(gameState.myField, '光')) {
      return false;
    }
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000325: 電光の浄化
   * 相手の場にいるモンスター1体の効果をターン終了時まで無効化する。自分の場に光属性モンスターがいるときライフを800回復する。
   * → 相手場にモンスター（効果無効化の対象）
   */
  C0000325: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000549: 落雷
   * 相手モンスター1体を選択。飛行モンスターなら破壊、それ以外は1000ダメージ。
   * → 相手場にモンスター（常に有効だが、飛行がいれば優先度高）
   * 注: 飛行がいなくても1000ダメージは与えられるので有効
   */
  C0000549: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 水属性・ヴェルゼファール/アクアレギナ系
  // ============================

  /**
   * C0000341: ヴェルゼファール降臨の儀式【刹那詠唱】
   * 自分の場にいる『ヴェルゼファール』モンスター1体をリリースし、デッキから『ヴェルゼファール』モンスター1体を場に召喚。
   * → 自場にヴェルゼファール + デッキにヴェルゼファールモンスター
   */
  C0000341: (card, gameState) => {
    if (!hasMonsterWithName(gameState.myField, 'ヴェルゼファール')) {
      return false;
    }
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('ヴェルゼファール')
    );
  },

  /**
   * C0000342: 漂流民の抵抗
   * 自分の水属性モンスター1体に「守護」を与える。自分の『アクアレギナ』モンスター全体のHPを1000アップ。
   * → 自場に水属性モンスター
   */
  C0000342: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.myField, '水');
  },

  /**
   * C0000343: アクアレギナの水晶術師の防壁
   * このターン、自分の『アクアレギナ』モンスターが受ける効果ダメージを0にし、HPを500アップ。
   * → 自場にアクアレギナモンスター
   */
  C0000343: (card, gameState) => {
    return hasMonsterWithName(gameState.myField, 'アクアレギナ');
  },

  /**
   * C0000344: シスラゴンの汚染
   * 相手モンスター1体の攻撃力を1000下げ、次のターン終了時まで行動不能にする。
   * → 相手場にモンスター
   */
  C0000344: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 光属性・ヴォランティス/鳥民系
  // ============================

  /**
   * C0000349: アヴィクルスの裁定【刹那詠唱】
   * 相手モンスター1体の攻撃力を1000下げ、次のターン終了時まで行動不能にする。
   * → 相手場にモンスター
   */
  C0000349: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000350: 鳥民の祈り
   * 自分のライフを800回復し、デッキから《アヴィクルス》フィールドカード1枚を手札に加える。
   * → デッキにアヴィクルスフィールドカード
   */
  C0000350: (card, gameState) => {
    return gameState.myDeck.some(
      c => c.type === 'field' && c.name?.includes('アヴィクルス')
    );
  },

  /**
   * C0000355: 鳥民の壮麗儀式
   * 自分の手札から《ヴォランティス》モンスター1枚を墓地に送り、デッキから《ヴォランティス》モンスター1枚を手札に加える。
   * → 手札にヴォランティスモンスター + デッキにヴォランティスモンスター
   */
  C0000355: (card, gameState) => {
    const hasInHand = gameState.myHand.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
    if (!hasInHand) {
      return false;
    }
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
  },

  /**
   * C0000358: アヴィクルスの風鳴り
   * 自分のデッキの上から4枚を見て、《ヴォランティス》カード1枚を手札に加え、残りを墓地に送る。
   * → デッキにヴォランティスカード（なければ全部墓地で無意味）
   */
  C0000358: (card, gameState) => {
    return gameState.myDeck.some(c => c.name?.includes('ヴォランティス'));
  },

  /**
   * C0000365: ヴォランティス・オーブ
   * 自分の場の《ヴォランティス》モンスター1体を選択し、その攻撃力を2500アップ。
   * → 自場にヴォランティスモンスター
   */
  C0000365: (card, gameState) => {
    return hasMonsterWithName(gameState.myField, 'ヴォランティス');
  },

  /**
   * C0000367: 秘島の風声
   * 自分の手札の《ヴォランティス》モンスター1体の召喚コストを2軽減し、そのカードと同名カードをデッキから手札に加える。
   * → 手札にヴォランティスモンスター
   */
  C0000367: (card, gameState) => {
    return gameState.myHand.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
  },

  /**
   * C0000372: 天翼の召喚儀式
   * 自分の手札から《ヴォランティス・オーブ》を1枚墓地に送り、自分の場の《鳥民》モンスター1体をリリースして、デッキから《ヴォランティス》モンスター1体を召喚。
   * → 手札にヴォランティス・オーブ + 場に鳥民モンスター + デッキにヴォランティスモンスター
   */
  C0000372: (card, gameState) => {
    const hasOrb = gameState.myHand.some(c => c.name === 'ヴォランティス・オーブ');
    if (!hasOrb) {
      return false;
    }
    if (!hasMonsterWithName(gameState.myField, '鳥民')) {
      return false;
    }
    return gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
  },

  // ============================
  // なし属性・虹羽密林系
  // ============================

  /**
   * C0000381: 虹羽密林の生命網【刹那詠唱】
   * 自分の墓地の《虹羽密林》モンスター2体を手札に戻す。
   * → 墓地に虹羽密林モンスター2体以上
   */
  C0000381: (card, gameState) => {
    const validMonsters = gameState.myGraveyard.filter(
      c => c.type === 'monster' && c.name?.includes('虹羽密林')
    );
    return validMonsters.length >= 2;
  },

  /**
   * C0000382: 虹羽の覚醒脈
   * 自分の場に《虹羽密林》モンスターが2体以上いる場合に発動可能。全てを墓地に送り、デッキ・手札・墓地から《ヴォランティス》モンスター1体を召喚。
   * → 場に虹羽密林モンスター2体以上 + デッキ/手札/墓地にヴォランティスモンスター
   */
  C0000382: (card, gameState) => {
    // 場に虹羽密林モンスター2体以上
    const rainbowCount = gameState.myField.filter(
      slot => slot !== null && slot.name?.includes('虹羽密林')
    ).length;
    if (rainbowCount < 2) {
      return false;
    }
    // デッキ/手札/墓地にヴォランティスモンスター
    const hasInDeck = gameState.myDeck.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
    const hasInHand = gameState.myHand.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
    const hasInGraveyard = gameState.myGraveyard.some(
      c => c.type === 'monster' && c.name?.includes('ヴォランティス')
    );
    return hasInDeck || hasInHand || hasInGraveyard;
  },

  // ============================
  // なし属性・対空系
  // ============================

  /**
   * C0000550: 撃墜命令
   * 相手の飛行モンスター1体を破壊する。
   * → 相手場に飛行モンスターが必要
   */
  C0000550: (card, gameState) => {
    return hasMonsterWithFlight(gameState.opponentField);
  },

  // ============================
  // 水属性・深海領域系
  // ============================

  /**
   * C0000528: 深海への招き
   * 「深海領域」を開放し、デッキの［ディープシャドウ］モンスター1枚を深海領域に送る。
   * → 深海領域が未開放、または深海領域に空きがありデッキに［ディープシャドウ］がいる
   */
  C0000528: (card, gameState) => {
    const { myDeepSeaZone, myDeck } = gameState;
    // 深海領域がない場合は無効
    if (!myDeepSeaZone) {
      return false;
    }
    // 深海領域が未開放なら常に有効（開放する効果があるため）
    if (!myDeepSeaZone.isUnlocked) {
      return true;
    }
    // 既に開放済みの場合、深海領域に空きがあり、デッキに［ディープシャドウ］がいるか
    if (myDeepSeaZone.cards.length >= myDeepSeaZone.maxCapacity) {
      return false;
    }
    return myDeck.some(c => c.type === 'monster' && c.category && c.category.includes('【ディープシャドウ】'));
  },

  /**
   * C0000529: 海底からの呼び声
   * 深海領域のカード1枚を浮上させる。この浮上コストは1軽減される。
   * → 深海領域にカードがある、フィールドに空きがある
   */
  C0000529: (card, gameState) => {
    const { myDeepSeaZone, myField } = gameState;
    // 深海領域にカードがない場合は無効
    if (!myDeepSeaZone || myDeepSeaZone.cards.length === 0) {
      return false;
    }
    // フィールドに空きがない場合は無効
    const hasEmptySlot = myField.some(slot => slot === null);
    return hasEmptySlot;
  },

  /**
   * C0000530: 深度加速
   * 深海領域の全カードに深度+2。
   * → 深海領域にカードがある
   */
  C0000530: (card, gameState) => {
    const { myDeepSeaZone } = gameState;
    return myDeepSeaZone !== null && myDeepSeaZone.cards.length > 0;
  },

  /**
   * C0000531: 荒海の強襲
   * 深海領域のカード1枚をコスト0で浮上させる。そのカードはこのターン攻撃力が2倍になる。ターン終了時、そのカードを破壊する。
   * → 深海領域にカードがある、フィールドに空きがある
   */
  C0000531: (card, gameState) => {
    const { myDeepSeaZone, myField } = gameState;
    // 深海領域にカードがない場合は無効
    if (!myDeepSeaZone || myDeepSeaZone.cards.length === 0) {
      return false;
    }
    // フィールドに空きがない場合は無効
    const hasEmptySlot = myField.some(slot => slot === null);
    return hasEmptySlot;
  },

  /**
   * C0000532: レヴィアタンの大時化
   * 「深海領域」を開放し、デッキのランダムな水属性カードを2枚を深海領域に送る。
   * → 深海領域が未開放、または深海領域に空きがありデッキに水属性カードがある
   */
  C0000532: (card, gameState) => {
    const { myDeepSeaZone, myDeck } = gameState;
    // 深海領域がない場合は無効
    if (!myDeepSeaZone) {
      return false;
    }
    // 深海領域が未開放なら常に有効（開放する効果があるため）
    if (!myDeepSeaZone.isUnlocked) {
      return true;
    }
    // 既に開放済みの場合、空きが0の場合は無効
    if (myDeepSeaZone.cards.length >= myDeepSeaZone.maxCapacity) {
      return false;
    }
    // デッキに水属性カードがあるか
    return myDeck.some(c => c.attribute === '水');
  },

  /**
   * C0000535: 大干ばつ
   * 相手の深海領域のカードを全て深度0で強制浮上させる。
   * → 相手の深海領域が開放されており、カードが1枚以上ある
   */
  C0000535: (card, gameState) => {
    const { opponentDeepSeaZone } = gameState;
    // 相手の深海領域がない、未開放、またはカードがない場合は無効
    if (!opponentDeepSeaZone || !opponentDeepSeaZone.isUnlocked || opponentDeepSeaZone.cards.length === 0) {
      return false;
    }
    return true;
  },

  /**
   * C0000537: 干上がる海
   * 相手の深海領域のカードを1枚破壊する。
   * → 相手の深海領域が開放されており、カードが1枚以上ある
   */
  C0000537: (card, gameState) => {
    const { opponentDeepSeaZone } = gameState;
    // 相手の深海領域がない、未開放、またはカードがない場合は無効
    if (!opponentDeepSeaZone || !opponentDeepSeaZone.isUnlocked || opponentDeepSeaZone.cards.length === 0) {
      return false;
    }
    return true;
  },

  /**
   * C0000552: 海を舐めた末路
   * 相手モンスター1体を破壊する。
   * このカードのコストは深海領域のカード枚数分、軽減される。
   * → 相手場にモンスターが1体以上必要
   */
  C0000552: (card, gameState) => {
    return hasAnyMonster(gameState.opponentField);
  },

  // ============================
  // 彷徨い関連魔法カード
  // ============================

  /**
   * C0000568: 魂散らし
   * コスト4以下の相手モンスター1体を【彷徨い】状態にする。
   * → 相手場にコスト4以下かつ飛行を持たないモンスターが必要
   */
  C0000568: (card, gameState) => {
    // 飛行モンスターは彷徨いにならない
    return gameState.opponentField.some(
      slot => slot !== null && (slot.cost || 0) <= 4 && !hasHikou(slot)
    );
  },

  /**
   * C0000569: 彷徨いの霧
   * 相手モンスター1体を【彷徨い】状態にする。さらに1枚ドロー。
   * → 相手場に飛行を持たないモンスターが必要（ドロー効果があるので緩和）
   * 注: ドロー効果もあるため、飛行のみでも発動は可能だが効率は悪い
   */
  C0000569: (card, gameState) => {
    // 飛行を持たないモンスターがいるか、またはドロー目的で発動できる
    const hasNonFlyingMonster = gameState.opponentField.some(
      slot => slot !== null && !hasHikou(slot)
    );
    // 彷徨い対象がいなくてもドロー効果があるので、モンスターさえいれば発動可能
    return hasAnyMonster(gameState.opponentField);
  },

  /**
   * C0000570: マリスの誘導
   * 【彷徨い】状態の相手モンスター1体のコントロールを得る。
   * ただし、自分の場に《ソウルカース》モンスターが1体以上いなければ発動できない。
   * → 自場にソウルカース + 相手場に彷徨い状態 + 自場に空き
   */
  C0000570: (card, gameState) => {
    // 自分の場にソウルカースモンスターがいるか
    const hasSoulCurse = gameState.myField.some(
      slot => slot !== null && slot.category?.includes('ソウルカース')
    );
    if (!hasSoulCurse) {
      return false;
    }

    // 自分の場に空きがあるか
    const hasEmptySlot = gameState.myField.some(slot => slot === null);
    if (!hasEmptySlot) {
      return false;
    }

    // 相手場に彷徨い状態のモンスターがいるか
    const hasWanderingOpponent = gameState.opponentField.some(slot => {
      if (!slot) return false;
      return slot.statusEffects?.some(
        (s: { type: string }) => s.type === 'wandering'
      );
    });

    return hasWanderingOpponent;
  },

  /**
   * C0000574: 聖火の救済
   * 自分のモンスター1体の状態異常を回復し、HPを500回復。
   * → 自場に状態異常を持つモンスター、またはHPが減っているモンスターが必要
   */
  C0000574: (card, gameState) => {
    return gameState.myField.some(slot => {
      if (!slot) return false;
      const hasStatus = slot.statusEffects && slot.statusEffects.length > 0;
      const canHeal = slot.currentHp < (slot.hp ?? slot.currentHp);
      return hasStatus || canHeal;
    });
  },

  /**
   * C0000575: 魂の帰還
   * 自分のモンスター1体の【彷徨い】状態を解除し、攻撃力を150%にする。
   * → 自場に彷徨い状態のモンスターが必要
   */
  C0000575: (card, gameState) => {
    return gameState.myField.some(slot => {
      if (!slot) return false;
      return slot.statusEffects?.some(
        (s: { type: string }) => s.type === 'wandering'
      );
    });
  },

  /**
   * C0000560: 遮光カーテン
   * 相手フィールドの光属性モンスター1体を手札に戻す。
   * → 相手場に光属性モンスターが必要
   */
  C0000560: (card, gameState) => {
    return hasMonsterWithAttribute(gameState.opponentField, '光');
  },

  // ============================
  // 条件なしカード（チェッカー不要）
  // C0000047 マーメイドの恵み - SP増加効果のため条件チェック不要
  // C0000193 一時の休息 - ライフ回復のみ
  // C0000207 知恵の代償 - ドローのみ
  // C0000255 未来の鴉予言 - デッキ確認・サーチのみ
  // ============================
};

/**
 * 魔法カードが有効かどうかをチェック
 */
export function isMagicEffective(
  card: CardWithId | null | undefined,
  gameState: AIGameState
): boolean {
  if (!card || !card.id) {
    return true; // チェックできない場合はデフォルトで有効
  }

  const checker = magicEffectivityCheckers[card.id];
  if (!checker) {
    return true; // チェッカーが未定義の場合はデフォルトで有効
  }

  try {
    return checker(card, gameState);
  } catch (error) {
    console.warn(`魔法カード有効性チェックでエラー: ${card.name}`, error);
    return true; // エラー時はデフォルトで有効
  }
}

/**
 * 使用可能な魔法カードリストから無効なカードを除外
 */
export function filterEffectiveMagicCards(
  magicCards: CardWithId[],
  gameState: AIGameState
): CardWithId[] {
  return magicCards.filter(card => {
    return isMagicEffective(card, gameState);
  });
}

export default {
  isMagicEffective,
  filterEffectiveMagicCards,
  // 外部からチェッカーを追加可能にする
  addChecker: (cardId: string, checker: MagicChecker) => {
    magicEffectivityCheckers[cardId] = checker;
  },
};
